import { useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Printer } from "lucide-react";
import { useBien } from "@/hooks/useBiens";
import { useBiensPermRole } from "@/hooks/useBiensPermRole";
import { useRole } from "@/contexts/RoleContext";
import { useAuth } from "@/contexts/AuthContext";
import { canAccessV2Path, getV2HomeForRole } from "@/v2/lib/v2Roles";
import { isForSale } from "@/lib/transaction";
import { getVenteOptions } from "@/v2/lib/venteFormFields";
import type { Bien, BienPhoto } from "@/types/bien";
import logoSrs from "@/assets/logo-skyline-renting-services.jpg";
import "./bienVenteExport.css";

/**
 * Vue « Exporter en PDF » de la fiche VENTE — réplique fidèle du modèle
 * client docs/QP H1D1 FR.pdf, imprimée via window.print() (zéro dépendance) :
 *
 *  - page 1 : logo, titre souligné « A vendre : … », Projet – Réf, adresse,
 *    prix souligné, photo de couverture, Description, tableau 11 lignes ;
 *  - page 2 : grille 2×4 des 8 photos suivantes ;
 *  - logo en tête et bloc contact en pied RÉPÉTÉS sur chaque page (thead/tfoot).
 *
 * La route vit HORS de V2Layout (pas de sidebar/nav à l'impression) → le
 * garde-fou rôle de V2Layout est réappliqué ici (même logique InboxRoute).
 * Le bloc contact reprend les coordonnées société du modèle ; la ligne
 * « Contact : » affiche l'Agent Skyline de la fiche (coordonnées personnelles
 * à mapper quand la liste des agents arrivera).
 */

/** Coordonnées société du bloc contact — invariantes (FR/NL/EN), reprises
 *  telles quelles du modèle docs/QP H1D1 FR.pdf. */
const SRS_CONTACT = {
  adresse: "Vorstlaan 292 Boulevard du Souverain – Oudergem 1160 Auderghem",
  telFax: "Tel.: 02/ 679 00 06  Fax: 02/ 679 00 01 – BTW/TVA : BE 0849 827 193",
  email: "info@skylinerenting.eu",
  site: "www.skylinerenting.eu",
};

/** "525000" | "525.000" | "1 724,5" → "525.000 EUR" (séparateur point comme
 *  le modèle). Texte inchangé si non numérique, "" si vide. */
function fmtEur(v: string | null | undefined): string {
  const s = (v ?? "").trim();
  if (!s) return "";
  const cleaned = s.replace(/\s/g, "").replace(/€|eur/gi, "");
  const normalized = cleaned.replace(/\./g, "").replace(",", ".");
  const n = parseFloat(normalized);
  if (!Number.isFinite(n) || !/^[\d.,]+$/.test(cleaned)) return s;
  return `${n.toLocaleString("de-DE", { maximumFractionDigits: 2 })} EUR`;
}

/** "2027-07-31" → "31/07/2027" (inchangé si autre format). */
function fmtDate(v: string | null | undefined): string {
  const s = (v ?? "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
}

const str = (v: string | null | undefined): string => (v ?? "").trim();

export function BienVenteExportPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permRole = useBiensPermRole();
  const { data: bien } = useBien(id, permRole);
  const { role } = useRole();
  const { profile, isDevSession } = useAuth();

  const OPTS = useMemo(() => getVenteOptions(t), [t]);

  // Garde-fou rôle (la route vit hors V2Layout qui porte ce check d'habitude).
  const roleReady = isDevSession || !!profile;
  if (roleReady && !canAccessV2Path(role, `/v2/biens/${id ?? ""}`)) {
    return <Navigate to={getV2HomeForRole(role)} replace />;
  }

  if (!bien) {
    return (
      <div className="min-h-screen bg-background py-16 text-center text-sm text-text-muted">
        {t("common.loading")}
      </div>
    );
  }

  // L'export n'existe que pour la fiche vente — un bien location est renvoyé
  // sur sa fiche.
  if (!isForSale(bien.transaction)) {
    return <Navigate to={`/v2/biens/${bien.id}`} replace />;
  }

  const b = bien as Bien;
  const optLabel = (options: { value: string; label: string }[], value: string): string =>
    options.find((o) => o.value === value)?.label ?? value;

  // ── Bloc titre (modèle : « A vendre : appartement 3 chambres meublé + parking ») ──
  const typeLabel = str(b.typeBien)
    ? optLabel(OPTS.typesBienVente, b.typeBien).toLocaleLowerCase(i18n.language)
    : "";
  const nbCh = parseInt(str(b.nbChambres), 10);
  const hasParking = ["Oui", "Inclus"].includes(str(b.parkingDisponible)) ||
    ["Oui", "Inclus"].includes(str(b.garageDisponible));
  const titleDetails = [
    typeLabel,
    Number.isFinite(nbCh) && nbCh > 0 ? t("vente.export.titleChambres", { count: nbCh }) : "",
    b.meuble === true ? t("vente.export.titleMeuble") : "",
    hasParking ? t("vente.export.titleParking") : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title = t("vente.export.title", { details: titleDetails });

  const projetLine = [str(b.projet), str(b.referenceInterne) || str(b.reference)]
    .filter(Boolean)
    .join(" – ");
  const adresseLine = [str(b.adresse), [str(b.codePostal), str(b.ville)].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(" – ");
  const prixLine = fmtEur(b.prix);

  // ── Description (langue UI, repli FR comme le modèle) ─────────────────────
  const lang = i18n.language.toLowerCase();
  const description =
    str(lang.startsWith("nl") ? b.descriptionNl : lang.startsWith("en") ? b.descriptionEn : b.descriptionFr) ||
    str(b.descriptionFr);

  // ── Tableau : les 11 lignes du modèle, dans son ordre exact ───────────────
  const etageVal =
    str(b.etage) && str(b.etagesTotal)
      ? `${str(b.etage)}/${str(b.etagesTotal)}`
      : str(b.etage);
  const rcPrecompteVal = [fmtEur(b.revenuCadastral), fmtEur(b.precompteImmobilier)]
    .filter(Boolean)
    .join(" / ");
  const loyerVal = fmtEur(b.dernierLoyer)
    ? `${fmtEur(b.dernierLoyer)}${str(b.dernierLoyerDate) ? ` (${fmtDate(b.dernierLoyerDate)})` : ""}`
    : "";
  // Cave / garage-emplacement : « Oui n° 54 (-2) » quand un n° est renseigné.
  const dispoNumero = (dispo: string, numero: string, options: { value: string; label: string }[]): string => {
    if (!dispo) return "";
    if (dispo === "Oui" && numero) return t("vente.export.ouiNumero", { num: numero });
    return optLabel(options, dispo);
  };
  const caveVal = dispoNumero(str(b.caveDisponible), str(b.caveNumero), OPTS.cave);
  const garageVal =
    dispoNumero(str(b.garageDisponible), str(b.garageNumero), OPTS.garage) ||
    dispoNumero(str(b.parkingDisponible), str(b.parkingNumero), OPTS.parking);
  // Disponibilité : slug vente (« À convenir »…) ; date précise → la date.
  // La colonne est HÉRITÉE de la location (date ISO) : un bien seedé avant la
  // convention slug peut porter une date brute → on la formate telle quelle.
  const dispoRaw = str(b.seraDispoPour);
  const dispoVal =
    dispoRaw === "date_precise" && str(b.venteDisponibiliteDate)
      ? fmtDate(b.venteDisponibiliteDate)
      : /^\d{4}-\d{2}-\d{2}$/.test(dispoRaw)
        ? fmtDate(dispoRaw)
        : dispoRaw
          ? optLabel(OPTS.seraDispoPour, dispoRaw)
          : "";

  const rows: { label: string; value: string }[] = [
    { label: t("vente.export.table.nbChambres"), value: str(b.nbChambres) },
    {
      label: t("vente.export.table.surface"),
      value: str(b.surfaceHabitable) ? t("vente.export.surfaceValue", { m2: str(b.surfaceHabitable) }) : "",
    },
    { label: t("vente.export.table.etage"), value: etageVal },
    {
      label: t("vente.export.table.meuble"),
      value: b.meuble === true ? t("common.yes") : b.meuble === false ? t("common.no") : "",
    },
    { label: t("vente.export.table.anneeConstruction"), value: str(b.anneeConstruction) },
    { label: t("vente.export.table.peb"), value: str(b.classePeb) },
    { label: t("vente.export.table.rcPrecompte"), value: rcPrecompteVal },
    { label: t("vente.export.table.loyerActuel"), value: loyerVal },
    { label: t("vente.export.table.cave"), value: caveVal },
    { label: t("vente.export.table.garageEmplacement"), value: garageVal },
    { label: t("vente.export.table.disponibilite"), value: dispoVal },
  ];

  // ── Photos : couverture en page 1, les 8 suivantes en grille page 2 ───────
  const photos = Array.isArray(b.photos) ? (b.photos as BienPhoto[]) : [];
  const cover = photos[0]?.url ?? "";
  const gridPhotos = photos.slice(1, 9);

  const agentName = str(b.agent);

  return (
    <div className="vente-export-screen min-h-screen bg-background">
      {/* @page uniquement quand cette vue est montée (global sinon) */}
      <style>{"@page { size: A4; margin: 0; }"}</style>

      {/* Barre d'actions (masquée à l'impression) */}
      <div className="no-print sticky top-0 z-10 bg-surface border-b border-border">
        <div className="max-w-[210mm] mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/v2/biens/${bien.id}`)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:text-accent hover:border-accent border border-border rounded-md cursor-pointer transition-colors outline-none shadow-none"
          >
            <ArrowLeft size={13} />
            {t("vente.export.backToFiche")}
          </button>
          <span className="text-sm font-semibold text-text-primary uppercase tracking-wider truncate">
            {str(b.referenceInterne) || str(b.reference) || str(b.adresse)}
          </span>
          <button
            type="button"
            onClick={() => window.print()}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-accent text-white rounded-md hover:opacity-90 transition-opacity cursor-pointer outline-none shadow-none"
          >
            <Printer size={13} />
            {t("vente.export.print")}
          </button>
        </div>
      </div>

      {/* Document A4 (thead/tfoot répétés sur chaque page imprimée) */}
      <div className="vente-export-sheet">
        <table className="vente-export-doc">
          <thead>
            <tr>
              <td>
                <img className="doc-header-logo" src={logoSrs} alt="Skyline Renting Services" />
              </td>
            </tr>
          </thead>
          <tfoot>
            <tr>
              <td>
                <div className="doc-footer-inner">
                  <img className="doc-footer-logo" src={logoSrs} alt="" />
                  <div className="doc-footer-contact">
                    {agentName && (
                      <p>
                        <span className="doc-contact-label">{t("vente.export.contact")}</span>{" "}
                        <b>{agentName}</b>
                      </p>
                    )}
                    <p>{SRS_CONTACT.adresse}</p>
                    <p>{SRS_CONTACT.telFax}</p>
                    <p>
                      {t("vente.export.emailLabel")}{" "}
                      <a href={`mailto:${SRS_CONTACT.email}`}>{SRS_CONTACT.email}</a>
                      {"  -  "}
                      {SRS_CONTACT.site}
                    </p>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
          <tbody>
            <tr>
              <td>
                <h1 className="doc-title">{title}</h1>
                {projetLine && <p className="doc-projet">{projetLine}</p>}
                {adresseLine && <p className="doc-adresse">{adresseLine}</p>}
                {prixLine && <p className="doc-prix">{prixLine}</p>}
                {cover && <img className="doc-cover" src={cover} alt="" />}
                {description && (
                  <>
                    <h2 className="doc-desc-heading">{t("vente.export.description")}</h2>
                    <p className="doc-desc">{description}</p>
                  </>
                )}
                <table className="doc-table">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.label}>
                        <td>{r.label}</td>
                        <td>{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {gridPhotos.length > 0 && (
                  <div className="doc-photos">
                    {gridPhotos.map((p) => (
                      <img key={p.id} src={p.url} alt="" />
                    ))}
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
